import { TourModel, useCancelTour } from 'api-hooks/tour/tour-list/tour-detail';
import { AuthorizationRules } from 'common/constants';
import { TourStatusEnum } from 'common/constants/enum';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useCancelTourAction(tour: TourModel) {
  const { t } = useTranslation();
  const { can } = useAuthorization();
  const dialog = useDialog();
  const cancelTourMutate = useCancelTour();
  const status = tour.status;
  const id = tour.id;
  const onDismiss = useDismiss('tours');
  const { onRequestApproval } = useApprovalAction('tours');

  const isAllowed = React.useMemo(() => {
    switch (status) {
      case TourStatusEnum.Active:
        return true;
      default:
        return false;
    }
  }, [status]);

  const cancelTourPermission = can(AuthorizationRules.ToursCancel) && isAllowed;

  const onCancelTour = React.useCallback(async () => {
    dialog.showConfirmation({
      message: t('ticketSales:confirmation_cancel_tour'),
      title: t('ticketSales:cancel_tour'),
      onPositiveAction: async (onClose) => {
        try {
          const result = await cancelTourMutate.mutateAsync({
            tourId: id,
          });

          notification.success({ message: result?.message });
          onDismiss(id);
          onClose();

          onRequestApproval(id);
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (onClose) => {
        onClose();
      },
    });
  }, [cancelTourMutate, dialog, id, onDismiss, onRequestApproval, t]);

  const cancelTourAction: PopoverMenuType = {
    key: `cancel-tour-${id}`,
    label: t('common:cancel_tour'),
    isError: true,
    isAllowed: cancelTourPermission,
    onClick: onCancelTour,
  };

  return {
    cancelTourAction,
    cancelTourMutate,
    onCancelTour,
    cancelTourPermission,
  };
}
